import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PayPalButton } from "react-paypal-button-v2";
import { motion, AnimatePresence } from "framer-motion";
import { usePlacesWidget } from "react-google-autocomplete";
import {
	Elements,
	PaymentElement,
	useStripe,
	useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { MapPin, CreditCard } from "lucide-react";

import {
	cancelPromoCode,
	saveCartSummary,
	savePaymentResult,
	saveShippingAddress,
} from "../actions/cartActions";
import {
	createOrder,
	createPaymentIntent,
	getRates,
} from "../actions/orderActions";
import {
	addEmailToCartMailChimp,
	getUserDetails,
	login,
} from "../actions/userActions";
import AffirmCheckout from "../components/AffirmCheckout";
import { Toast } from "./customize/Toast";
import { FormInput } from "../components/FormInput";
import { CartSummary } from "../components/CartSummary";
import { calculateTotals } from "../utils/calulateTotals";

const stripePromise = loadStripe(process.env.REACT_APP_PUBLIC_STRIPE);

const StripePaymentComponent = ({ successPaymentHandler, payer }) => {
	const [isProcessing, setIsProcessing] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	const stripe = useStripe();
	const elements = useElements();

	const handleSubmit = async (e) => {
		if (!stripe || !elements) {
			console.error("Stripe.js has not loaded.");
			// Stripe.js hasn't yet loaded.
			// Make sure to disable form submission until Stripe.js has loaded.
			return;
		}

		setIsProcessing(true);

		const { error, paymentIntent } = await stripe.confirmPayment({
			elements,
			redirect: "if_required",
		});

		// This point will only be reached if there is an immediate error when
		// confirming the payment. Otherwise, your customer will be redirected to
		// your `return_url`. For some payment methods like iDEAL, your customer will
		// be redirected to an intermediate site first to authorize the payment, then
		// redirected to the `return_url`.
		if (error?.type === "card_error" || error?.type === "validation_error") {
			setErrorMessage(error.message);
		} else if (paymentIntent && paymentIntent.status === "succeeded") {
			console.log(paymentIntent);
			successPaymentHandler({
				status: "COMPLETED",
				id: paymentIntent.id,
				payer: payer,
				purchase_units: [
					{
						amount: {
							value: paymentIntent.amount / 100,
							currency_code: paymentIntent.currency,
						},
					},
				],
			});
		} else {
			setErrorMessage("An unexpected error occurred.");
		}

		setIsProcessing(false);
	};

	const paymentElementOptions = {
		layout: "tabs",
	};

	return (
		<div>
			<PaymentElement options={paymentElementOptions} />
			<button
				type="button"
				onClick={handleSubmit}
				disabled={isProcessing || !stripe}
				className="w-full bg-[#BE2026] text-white p-3 my-4"
			>
				{isProcessing ? "Processing..." : "Pay"}
			</button>

			{errorMessage && <div className="text-red-500 mt-2">{errorMessage}</div>}
		</div>
	);
};

const PaymentMethodCard = ({ selected, onClick, icon, name, children }) => (
	<label className="cursor-pointer">
		<input
			type="radio"
			className="hidden"
			checked={selected}
			onChange={onClick}
		/>
		<div
			className={`p-4 rounded-xl border transition-colors ${
				selected ? "border-primary bg-primary/5" : "border-gray-200"
			}`}
		>
			<div className="flex flex-col items-center gap-2">
				{icon}
				<span className="text-sm font-medium">{name}</span>
				{children}
			</div>
		</div>
	</label>
);

const buildParcels = (cartItems) => {
	return cartItems.map((item) => {
		const weight = { value: item.optionInfo.widthWhole * 0.15, unit: "kg" };
		const dimension = {
			width: 7,
			height: 7,
			depth: Number(item.optionInfo.widthWhole),
			unit: "in",
		};
		let quantity = item.optionInfo.quantity;

		return {
			description: "Food XS",
			box_type: "custom",
			weight,
			dimension,
			items: [
				{
					description: "Food Bar",
					origin_country: "JPN",
					quantity: quantity,
					price: { amount: 3, currency: "CAD" },
					weight: { value: 0.6, unit: "kg" },
					sku: "PS4-2015",
				},
			],
		};
	});
};

const buildShippingAddress = (shippingAddress, countryState) => {
	return {
		contact_name: shippingAddress.firstName + " " + shippingAddress.lastName,
		street1: shippingAddress.addressLine1,
		street2: shippingAddress.addressLine2,
		city: shippingAddress.city,
		state: shippingAddress.province,
		postal_code: shippingAddress.postalCode.replace(/\s/g, ""),
		country: countryState,
		phone: shippingAddress.phoneNumber,
		email: shippingAddress.email,
		type: "residential",
	};
};

const PlaceOrderScreen = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [loading, setLoading] = useState(false);
	const [clientSecret, setClientSecret] = useState("");
	const [paymentError, setPaymentError] = useState("");
	const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("stripe");
	const [isPaymentEnabled, setIsPaymentEnabled] = useState(false);
	const orderCreate = useSelector((state) => state.orderCreate);
	const { order: { insertId } = {} } = orderCreate;
	const { cartItems, cartSummary } = useSelector((state) => state.cart);
	const { userInfo } = useSelector((state) => state.userLogin);
	const isGuest = useSelector((state) => state.userLogin.isGuest);
	const countryState = useSelector((state) => state.country);
	const [dpmCheckerLink, setDpmCheckerLink] = useState("");

	// Form state
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: userInfo?.email || "",
		phoneNumber: "",
		addressLine1: "",
		addressLine2: "",
		city: "",
		province: "",
		postalCode: "",
		country: countryState,
		howDidYouHear: "",
	});

	const [formErrors, setFormErrors] = useState({});
	const [shipping, setShipping] = useState(0);

	// Google Places integration
	const { ref } = usePlacesWidget({
		apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
		onPlaceSelected: (place) => {
			const components = place.address_components;
			const addressMapping = {
				street_number: "street_number",
				route: "route",
				locality: "city",
				administrative_area_level_1: "province",
				postal_code: "postalCode",
			};

			const addressData = {};
			components.forEach((component) => {
				const type = component.types[0];
				if (addressMapping[type]) {
					addressData[addressMapping[type]] =
						addressMapping[type] === "province"
							? component.short_name
							: component.long_name;
				}
			});

			setFormData((prev) => ({
				...prev,
				addressLine1: `${addressData.street_number} ${addressData.route}`,
				city: addressData.city,
				province: addressData.province,
				postalCode: addressData.postalCode,
			}));
		},
		options: {
			types: ["address"],
			componentRestrictions: { country: countryState === "USA" ? "us" : "ca" },
		},
	});

	useEffect(() => {
		// Reset address when country changes
		setFormData((prev) => ({
			...prev,
			country: countryState,
			addressLine1: "",
			addressLine2: "",
			city: "",
			province: "",
			postalCode: "",
		}));
	}, [countryState]);

	useEffect(() => {
		if (!isGuest) dispatch(getUserDetails(userInfo, navigate));
	}, [dispatch, userInfo, navigate]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);

		try {
			// Handle guest email if not logged in
			if (!userInfo) {
				try {
					await dispatch(login(formData.email, null));
					await addEmailToCartMailChimp(formData.email);
				} catch (error) {
					console.log("Error processing guest email:", error);
					setLoading(false);
					return;
				}
			}

			await addEmailToCartMailChimp(userInfo.email);

			// Validate form
			const errors = {};
			if (!formData.email) errors.email = "Email is required";
			if (!formData.firstName) errors.firstName = "First name is required";
			if (!formData.lastName) errors.lastName = "Last name is required";
			if (!formData.phoneNumber)
				errors.phoneNumber = "Phone number is required";
			if (!formData.addressLine1) errors.addressLine1 = "Address is required";
			if (!formData.city) errors.city = "City is required";
			if (!formData.province) errors.province = "Province/State is required";
			if (!formData.postalCode) errors.postalCode = "Postal code is required";

			if (Object.keys(errors).length > 0) {
				setFormErrors(errors);
				setLoading(false);
				return;
			}

			// Save shipping address
			await dispatch(saveShippingAddress(formData));

			// Get shipping rates
			let parcels = buildParcels(cartItems);
			let ship_to = buildShippingAddress(formData, countryState);

			dispatch(
				getRates(
					{
						parcels,
						ship_to,
					},
					navigate
				)
			).then(async (rates) => {
				if (!rates) {
					console.log("Error calculating shipping rates");
					setLoading(false);
					setPaymentError("Error calculating shipping rates");
					return;
				}

				const newShipping =
					countryState === "USA" ? rates.ground.total_charge.amount : 0;

				const newTotals = calculateTotals(
					cartItems,
					countryState,
					newShipping,
					cartSummary?.withPromoPrice,
					formData.province
				);
				setShipping(newShipping);

				const cartSummaryUpdate = {
					...cartSummary,
					shipping,
					FINAL_MSRP: newTotals.msrp,
					FINAL_PRICE: newTotals.ourPrice,
					FINAL_TAX: newTotals.tax,
					SUBTOTAL: newTotals.subTotal - newTotals.tax - newShipping,
					TOTAL: newTotals.subTotal,
					SAVINGS: newTotals.savings,
					email: formData.email,
				};

				await dispatch(saveCartSummary(cartSummaryUpdate));

				// Create initial order with isSubmitted: 0
				const orderData = {
					cartItems,
					cartSummary: cartSummaryUpdate,
					shippingAddress: formData,
					isSubmitted: 0,
					howDidYouHear: formData.howDidYouHear,
					email: formData.email,
				};
				await dispatch(createOrder(orderData));
				// Proceed to payment
				if (selectedPaymentMethod === "stripe") {
					createPaymentIntent({
						currency: formData.country === "USA" ? "usd" : "cad",
						shipping: parseFloat(Math.round(shipping * 100) / 100) * 100,
						amount:
							(Math.round(
								calculateTotals(
									cartItems,
									countryState,
									shipping,
									cartSummary?.withPromoPrice,
									formData.province
								).subTotal.toFixed(2) * 100
							) /
								100) *
							100,
						items: cartItems.map((item) => {
							let price =
								formData.country === "USA"
									? parseFloat(item.optionInfo.OUR_PRICE * 0.75) *
									  (1 + newTotals.taxRate)
									: item.optionInfo.OUR_PRICE * (1 + newTotals.taxRate);

							// Convert to cents
							// First, round to 2 decimal places
							price = Math.round(price * 100) / 100;
							// Then multiply by 100 to get cents
							price = price * 100;

							// Remove decimal point if it exists
							return {
								quantity: item.optionInfo.quantity,
								amount: price,
							};
						}),
					}).then((data) => {
						setClientSecret(data.clientSecret);
						setDpmCheckerLink(data.dpmCheckerLink);
					});
				}

				setLoading(false);
				setIsPaymentEnabled(true);
			});
		} catch (error) {
			console.error("Error processing order:", error);
			setLoading(false);
		}
	};

	const handlePaymentSuccess = async (paymentResult) => {
		try {
			if (paymentResult.status !== "COMPLETED") {
				setPaymentError("Payment failed");
				return;
			}
			addEmailToCartMailChimp(formData.email);

			setLoading(true);
			// Update order with isSubmitted: 1 after successful payment
			await dispatch(
				createOrder({
					cartItems,
					cartSummary,
					shippingAddress: formData,
					isSubmitted: 1,
					insertId,
					email: formData.email,
					howDidYouHear: formData.howDidYouHear,
				})
			).then(async () => {
				await dispatch(savePaymentResult(paymentResult));
				await dispatch(
					cancelPromoCode(userInfo?.id || -1, cartSummary?.promoCode)
				);
				navigate("/confirmation", { state: { prevPath: "placeorder" } });
			});
		} catch (error) {
			console.log("Payment error:", error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gray-50 py-8">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				<div className="flex items-center gap-3 mb-8">
					<MapPin className="w-8 h-8 text-primary" />
					<h1 className="text-2xl font-bold">Checkout</h1>
				</div>
				<Toast
					show={paymentError}
					message={paymentError}
					onClose={() => setPaymentError("")}
				/>

				<form
					onSubmit={handleSubmit}
					className="grid grid-cols-1 lg:grid-cols-2 gap-8"
				>
					<div className="lg:col-span-1 space-y-8">
						{/* Contact Information */}
						<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
							<h2 className="text-lg font-medium mb-4">Contact Information</h2>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<FormInput
									label="Email"
									type="email"
									value={formData.email}
									onChange={(e) =>
										setFormData({ ...formData, email: e.target.value })
									}
									required
									disabled={isPaymentEnabled}
									error={formErrors.email}
								/>
								<FormInput
									label="Phone Number"
									type="tel"
									value={formData.phoneNumber}
									onChange={(e) =>
										setFormData({
											...formData,
											phoneNumber: e.target.value,
										})
									}
									required
									error={formErrors.phoneNumber}
									disabled={isPaymentEnabled}
								/>
							</div>
						</div>

						{/* Shipping Address */}
						<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
							<h2 className="text-lg font-medium mb-4">Shipping Address</h2>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<FormInput
									label="First Name"
									value={formData.firstName}
									onChange={(e) =>
										setFormData({ ...formData, firstName: e.target.value })
									}
									required
									error={formErrors.firstName}
									disabled={isPaymentEnabled}
								/>
								<FormInput
									label="Last Name"
									value={formData.lastName}
									onChange={(e) =>
										setFormData({ ...formData, lastName: e.target.value })
									}
									required
									error={formErrors.lastName}
									disabled={isPaymentEnabled}
								/>
							</div>

							<div className="mt-4">
								<FormInput
									label="Address"
									ref={ref}
									value={formData.addressLine1}
									onChange={(e) =>
										setFormData({
											...formData,
											addressLine1: e.target.value,
										})
									}
									required
									error={formErrors.addressLine1}
									disabled={isPaymentEnabled}
								/>
							</div>

							<div className="mt-4">
								<FormInput
									label="Apartment, suite, etc. (optional)"
									value={formData.addressLine2}
									onChange={(e) =>
										setFormData({
											...formData,
											addressLine2: e.target.value,
										})
									}
									disabled={isPaymentEnabled}
								/>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
								<FormInput
									label="City"
									value={formData.city}
									onChange={(e) =>
										setFormData({ ...formData, city: e.target.value })
									}
									required
									error={formErrors.city}
									disabled={isPaymentEnabled}
								/>
								<FormInput
									maxLength={2}
									label="Province/State"
									value={formData.province}
									onChange={(e) =>
										setFormData({ ...formData, province: e.target.value })
									}
									required
									error={formErrors.province}
									disabled={isPaymentEnabled}
								/>
								<FormInput
									label="Postal Code"
									value={formData.postalCode}
									onChange={(e) =>
										setFormData({ ...formData, postalCode: e.target.value })
									}
									required
									error={formErrors.postalCode}
									disabled={isPaymentEnabled}
								/>
							</div>
						</div>

						{/* How did you hear about us? */}
						<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
							<h2 className="text-lg font-medium mb-4">
								How did you hear about us?
							</h2>
							<select
								aria-label="How did you hear about us?"
								value={formData.howDidYouHear}
								onChange={(e) =>
									setFormData({
										...formData,
										howDidYouHear: e.target.value,
									})
								}
								required
								disabled={isPaymentEnabled}
								className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
							>
								<option value="">Select an option</option>
								<option value="Facebook">Facebook</option>
								<option value="Instagram">Instagram</option>
								<option value="Social Media">Social Media</option>
								<option value="Search Engine">Search Engine</option>
								<option value="Advertisement">Advertisement</option>
								<option value="Word of mouth">Word of mouth</option>
								<option value="Other">Other</option>
							</select>
						</div>
					</div>
					{/* Order Summary Section */}
					<div className="lg:col-span-1">
						<div className=" sticky top-4 space-y-6">
							<CartSummary
								totals={calculateTotals(
									cartItems,
									countryState,
									shipping,
									cartSummary?.withPromoPrice,
									formData.province
								)}
								promoCode={cartSummary?.promoCode}
								promoAmount={cartSummary?.withPromoPrice}
								shipping={shipping}
							/>

							{/* Payment Methods */}
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
								{isPaymentEnabled ? (
									<div className="lg:col-span-2">
										{/* Payment Section */}
										{selectedPaymentMethod === "stripe" &&
											clientSecret.trim() !== "" && (
												<Elements
													stripe={stripePromise}
													options={{ clientSecret }}
												>
													<StripePaymentComponent
														successPaymentHandler={handlePaymentSuccess}
														dpmCheckerLink={dpmCheckerLink}
														payer={{
															name: {
																given_name: formData.firstName,
																surname: formData.lastName,
															},
															email_address: formData.email,
														}}
													/>
												</Elements>
											)}

										{selectedPaymentMethod === "paypal" && !loading && (
											<div className="mt-8 max-w-2xl mx-auto">
												<PayPalButton
													shippingPreference="NO_SHIPPING"
													options={{
														locale:
															formData.country === "USA" ? "en_US" : "en_CA",
														currency:
															formData.country === "USA" ? "USD" : "CAD",
														clientId:
															process.env.NODE_ENV === "production"
																? process.env.REACT_APP_PRODUCTION_PAYPAL_ID
																: process.env.REACT_APP_SANDBOX_PAYPAL_ID,
													}}
													amount={calculateTotals(
														cartItems,
														countryState,
														shipping,
														cartSummary?.withPromoPrice,
														formData.province
													).subTotal.toFixed(2)}
													style={{
														shape: "pill",
														layout: "vertical",
													}}
													onSuccess={handlePaymentSuccess}
												/>
											</div>
										)}

										{selectedPaymentMethod === "affirm" &&
											!loading &&
											formData.country === "Canada" && (
												<div className="mt-8 max-w-2xl mx-auto">
													<AffirmCheckout onSuccess={handlePaymentSuccess} />
												</div>
											)}
									</div>
								) : (
									<>
										<h2 className="text-lg font-medium mb-4">Payment Method</h2>
										<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
											{/* Stripe */}
											<PaymentMethodCard
												selected={selectedPaymentMethod === "stripe"}
												onClick={() => setSelectedPaymentMethod("stripe")}
												icon={<CreditCard className="w-6 h-6" />}
												name="Credit Card"
											/>

											<PaymentMethodCard
												selected={selectedPaymentMethod === "paypal"}
												onClick={() => setSelectedPaymentMethod("paypal")}
												icon={<CreditCard className="w-6 h-6" />}
												name="PayPal"
											/>

											{formData.country === "Canada" && (
												<PaymentMethodCard
													selected={selectedPaymentMethod === "affirm"}
													onClick={() => setSelectedPaymentMethod("affirm")}
													icon={
														<img
															src="/images/affirm.png"
															alt="Affirm"
															className="h-6"
														/>
													}
													name="Affirm"
												/>
											)}
										</div>
									</>
								)}
							</div>
							<div className="flex justify-between">
								<button
									type="button"
									onClick={() => navigate("/cart")}
									className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-colors"
								>
									Return to Cart
								</button>
								{!isPaymentEnabled && (
									<button
										type="submit"
										disabled={loading}
										className="px-6 py-3 bg-primary text-white font-medium rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
									>
										{loading ? (
											<span className="flex items-center gap-2">
												<svg
													className="animate-spin h-5 w-5"
													viewBox="0 0 24 24"
												>
													<circle
														className="opacity-25"
														cx="12"
														cy="12"
														r="10"
														stroke="currentColor"
														strokeWidth="4"
														fill="none"
													/>
													<path
														className="opacity-75"
														fill="currentColor"
														d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
													/>
												</svg>
												Processing...
											</span>
										) : (
											"Pay Now"
										)}
									</button>
								)}
							</div>
						</div>
					</div>
				</form>

				{/* Loading Overlay */}
				<AnimatePresence>
					{loading && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
						>
							<div className="bg-white rounded-xl p-8 flex flex-col items-center gap-4">
								<div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
								<p className="text-lg font-medium">Please wait...</p>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</div>
	);
};

export default PlaceOrderScreen;
